import { TestDetailDrawer } from '@agilelab/plugin-wb-governance';
import {
  EnumFilter,
  TableCellProps,
  TestStatus,
  WbResponsiveTableToolbar,
  WbTable,
  WbTruncatedTypographyWrapper,
  getObjectPropFromString,
  useDrawersContext,
} from '@agilelab/plugin-wb-platform';
import { makeStyles, Typography } from '@material-ui/core';
import { format, parseISO } from 'date-fns';
import _ from 'lodash';
import { useMemo, useState } from 'react';
import { AsyncState } from 'react-use/lib/useAsync';
import { PolicyItem, usePolicyTab } from './PoliciesTabContext';
import clsx from 'clsx';
import { useFlagAndScoreContext } from '../../FlagAndScoreContext';

export interface Filters {
  text?: string;
  result?: string[];
  timing?: string[];
}

const useStyles = makeStyles(
  theme => ({
    table: {
      flex: '1 1 0',
    },
    content: {
      height: '100%',
      display: 'flex',
      gap: theme.spacing(1),
      flexDirection: 'column',
    },
  }),
  { name: 'Technologies' },
);

type FilterField = 'result' | 'timing';

const changeFilter = (
  field: FilterField,
  value: string[] | undefined,
  setFilters: (filters: Filters) => void,
  currentFilters: Filters,
) => {
  setFilters({ ...currentFilters, [field]: value });
};

const columns: TableCellProps<PolicyItem>[] = [
  {
    field: 'name',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper
        value={`${field.name} v:${field.version}`}
      />
    ),
    headerName: 'Policy',
    cellProps: {
      width: '18%',
    },
    sortable: true,
  },

  {
    field: 'timing',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper value={field.timing} />
    ),
    headerName: 'Timing',
    cellProps: {
      width: '10%',
    },
    sortable: true,
  },

  {
    field: 'description',
    fieldRender: (field: any) => (
      <WbTruncatedTypographyWrapper value={field.description} />
    ),
    headerName: 'Description',
    cellProps: {
      width: '20%',
    },
    sortable: true,
  },
  {
    field: 'date',
    fieldRender: field => (
      <Typography component="div">
        {format(parseISO(field.date), 'yyyy/MM/dd HH:mm:ss')}
      </Typography>
    ),
    headerName: 'Date',
    cellProps: {
      width: '23%',
    },
    sortable: true,
  },
  {
    field: 'result',
    fieldRender: (field: any) => {
      return <TestStatus status={field.result} />;
    },
    headerName: 'Result',
    cellProps: {
      width: '20%',
    },
    sortable: true,
  },
];

export const PoliciesTable = ({
  policyViolationsState,
  tableClassname,
  tableStyle,
}: {
  policyViolationsState: AsyncState<PolicyItem[] | undefined>;
  tableStyle?: React.CSSProperties;
  tableClassname?: string;
}) => {
  const { activeEntityExternalId } = usePolicyTab();
  const { descendants, root } = useFlagAndScoreContext();

  const selectedEntity = useMemo(() => {
    if (root.externalId === activeEntityExternalId) {
      return root;
    }
    if (activeEntityExternalId) {
      return descendants.get(activeEntityExternalId);
    }
    return undefined;
  }, [activeEntityExternalId, descendants, root]);

  const classes = useStyles();
  const { drawers, toggleBoolean } = useDrawersContext();
  const [evaluationId, setEvaluationId] = useState<string>();
  const [filters, setFilters] = useState<Filters>({});
  const [pagination, setPagination] = useState({
    limit: 25,
    offset: 0,
  });
  const resultsOptions = ['Info', 'Warning', 'Error'];
  const timingOptions = ['Runtime', 'Deployment'];

  const dataTable = useMemo(() => {
    if (!policyViolationsState.value) {
      return [];
    }

    const fields = columns.map(col => col.field || '');
    Object.keys(filters).forEach(key =>
      filters[key as keyof Filters] === undefined
        ? delete filters[key as keyof Filters]
        : {},
    );
    const filtered = policyViolationsState?.value?.filter(row => {
      const rowItems = fields.map(field =>
        getObjectPropFromString(row, field).toLowerCase(),
      );

      return rowItems.find(
        item => item && item.includes(filters?.text?.toLowerCase() ?? ''),
      );
    });

    let filteredData = filtered;
    if (filters.result || filters.timing) {
      const filterResults = (filters.result || []).map(res =>
        _.capitalize(res.toLocaleLowerCase()),
      );

      const filterTimings = (filters.timing || []).map(tim =>
        _.capitalize(tim.toLocaleLowerCase()),
      );

      filteredData = filteredData.filter(row => {
        const rowResult = _.capitalize(row.result?.toLocaleLowerCase() ?? '');
        const rowTiming = _.capitalize(row.timing?.toLocaleLowerCase() ?? '');

        const resultMatches =
          filterResults.length === 0 || filterResults.includes(rowResult);
        const timingMatches =
          filterTimings.length === 0 || filterTimings.includes(rowTiming);

        return resultMatches && timingMatches;
      });
    }
    return filteredData;
  }, [policyViolationsState.value, filters]);

  const filterConfigurations = [
    {
      field: 'Result',
      options: resultsOptions,
      value: filters.result,
      onChange: (result: string[] | undefined) =>
        changeFilter('result', result, setFilters, filters),
      onSearch: (v: string | RegExp) =>
        resultsOptions.filter(o => new RegExp(v, 'ig').test(o)),
    },
    {
      field: 'Timing',
      options: timingOptions,
      value: filters.timing,
      onChange: (timing: string[] | undefined) =>
        changeFilter('timing', timing, setFilters, filters),
      onSearch: (v: string | RegExp) =>
        timingOptions.filter(o => new RegExp(v, 'ig').test(o)),
    },
  ];

  const sortedRows = dataTable.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  return (
    <>
      <Typography style={{ fontWeight: 500 }} gutterBottom>
        {selectedEntity?.displayName}
      </Typography>
      <WbResponsiveTableToolbar
        searchProps={{
          value: filters.text,
          onChange: search => {
            setFilters({ text: search });
          },
        }}
        marginBottom
        onClear={() => setFilters({})}
      >
        {filterConfigurations.map((config, index) => (
          <EnumFilter<string>
            key={index}
            field={config.field}
            options={config.options}
            onChange={config.onChange}
            value={config.value}
            renderOption={(o: any) => o}
            renderValue={(o: any) => o}
            onSearch={config.onSearch}
          />
        ))}
      </WbResponsiveTableToolbar>
      <WbTable<PolicyItem>
        classNames={{ container: clsx(tableClassname, classes.table) }}
        styles={{ container: tableStyle }}
        components={{
          tableLoader: { loading: policyViolationsState.loading },
          tableContent: {
            columns,
            rows: sortedRows,
            onRowClick: e => {
              setEvaluationId(e.id);
              toggleBoolean(1);
            },
          },
        }}
        pagination={{
          count: dataTable?.length || 0,
          limit: pagination.limit,
          offset: pagination.offset,
          onPageChange: (page: number) => {
            setPagination({
              ...pagination,
              offset: page * pagination.limit,
            });
          },
          onRowsPerPageChange: (rowsPerPage: number) => {
            setPagination({
              offset: 0,
              limit: rowsPerPage,
            });
          },
        }}
      />

      <TestDetailDrawer
        evaluationId={evaluationId!}
        idType="external"
        open={drawers.get(1) ?? false}
        setOpen={() => toggleBoolean(1)}
      />
    </>
  );
};
